import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSEO, getOrganizationSchema, getBreadcrumbSchema } from '../../utils/seo';
import SiteFooter from '../../components/feature/SiteFooter';
import SiteNav from '../../components/feature/SiteNav';
import PageHero, { gradientTextStyle } from '../../components/feature/PageHero';
import { trackPhoneConversion } from '../../utils/analytics';

const GRADIENT = 'linear-gradient(135deg, #14b8a6 0%, #10b981 100%)';

export default function Booking() {
  const navigate = useNavigate();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://seitenschmied.com';
  const [formData, setFormData] = useState({ name: '', phone: '', email: '', subject: '', message: '' });
  const [contactMethod, setContactMethod] = useState<'phone' | 'email'>('phone');
  const [formError, setFormError] = useState('');
  const [formSubmitting, setFormSubmitting] = useState(false);

  useSEO({
    title: 'Termin buchen – Kostenloses Erstgespräch | Seitenschmied',
    description: 'Buchen Sie jetzt Ihr kostenloses 30-minütiges Erstgespräch mit Seitenschmied. Ehrliche Beratung zu Ihrer neuen Website – ohne Verkaufsdruck. Online oder persönlich.',
    keywords: 'termin buchen, kostenloses erstgespräch, website beratung, webdesign beratung, seitenschmied kontakt',
    canonical: `${siteUrl}/termin-buchen`,
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        getOrganizationSchema(),
        {
          '@type': 'WebPage',
          '@id': `${siteUrl}/termin-buchen#webpage`,
          url: `${siteUrl}/termin-buchen`,
          name: 'Termin buchen – Kostenloses Erstgespräch | Seitenschmied',
          inLanguage: 'de-DE',
        },
        getBreadcrumbSchema([
          { name: 'Startseite', url: siteUrl },
          { name: 'Termin buchen', url: `${siteUrl}/termin-buchen` },
        ]),
      ],
    },
  });

  useEffect(() => {
    if (!window.location.hash) {
      window.scrollTo(0, 0);
    } else {
      const id = window.location.hash.replace('#', '');
      setTimeout(() => {
        const el = document.getElementById(id);
        el?.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    }
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      if (document.body.contains(script)) document.body.removeChild(script);
    };
  }, []);

  const scrollToCalendly = () => {
    const el = document.getElementById('calendly-section');
    el?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.subject || !formData.message || (contactMethod === 'phone' ? !formData.phone : !formData.email)) {
      setFormError('Bitte füllen Sie alle Pflichtfelder aus.');
      return;
    }
    if (formData.message.length > 500) {
      setFormError('Die Nachricht darf maximal 500 Zeichen enthalten.');
      return;
    }
    setFormSubmitting(true);
    try {
      const formElement = e.target as HTMLFormElement;
      const fd = new FormData(formElement);
      const response = await fetch('https://readdy.ai/api/form/d5s9oqs6pqudvsuu51ug', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(fd as never).toString(),
      });
      if (response.ok) {
        navigate('/danke');
      } else {
        setFormError('Es gab einen Fehler. Bitte versuchen Sie es erneut.');
      }
    } catch {
      setFormError('Es gab einen Fehler. Bitte versuchen Sie es erneut.');
    } finally {
      setFormSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Inter', sans-serif" }}>

      <SiteNav activePage="booking" navId="booking-nav" />

      <PageHero
        label="Kostenloses Erstgespräch"
        headline={
          <>
            Reden und<br />
            <span style={gradientTextStyle}>Pläne schmieden.</span>
          </>
        }
        subtext="30 Minuten. Ihre Wünsche. Ein klarer Plan. Keine Verpflichtungen, kein Verkaufsdruck – nur ehrliche Beratung."
        backgroundImage="https://readdy.ai/api/search-image?query=dramatic%20cinematic%20close%20up%20of%20glowing%20molten%20metal%20being%20shaped%20by%20skilled%20craftsman%20hands%20in%20dark%20forge%20workshop%20intense%20orange%20and%20teal%20light%20contrast%20sparks%20flying%20dramatic%20depth%20of%20field%20ultra%20sharp%20editorial%20photography%20dark%20industrial%20atmosphere%20with%20strong%20bokeh%20and%20rich%20shadows%20highly%20detailed%20high%20contrast%20moody%20cinematic%20color%20grading%20professional%20studio%20quality&width=1920&height=1080&seq=booking-hero-forge-v3&orientation=landscape"
        backgroundPosition="center"
        ctas={[
          { label: 'Termin wählen', icon: 'ri-calendar-check-line', onClick: scrollToCalendly, variant: 'primary' },
          { label: 'Direkt anrufen', icon: 'ri-phone-line', href: 'tel:+491607676763', variant: 'secondary' },
        ]}
      >
        {/* Trust badges */}
        <div className="flex flex-wrap gap-4 sm:gap-6 mt-8 sm:mt-10">
          {[
            { icon: 'ri-shield-check-line', label: '100 % kostenlos' },
            { icon: 'ri-video-line', label: 'Online oder vor Ort' },
          ].map((badge) => (
            <div key={badge.label} className="flex items-center gap-2 text-white/60">
              <i className={`${badge.icon} text-base`} style={{ color: '#14b8a6' }}></i>
              <span className="text-sm font-medium">{badge.label}</span>
            </div>
          ))}
        </div>
      </PageHero>

      {/* ── WAS SIE ERWARTET ── */}
      <section className="py-14 sm:py-24 lg:py-32 px-4 sm:px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <p className="ss-label" style={{ color: '#14b8a6' }}>
            Was Sie erwartet
          </p>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-black text-gray-900 leading-[1.05] mb-10 sm:mb-16 lg:mb-24 max-w-3xl"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Ein Gespräch{' '}
            <span style={{
              background: GRADIENT,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>
              auf Augenhöhe.
            </span>
          </h2>

          <div className="space-y-0">
            {[
              { n: '01', title: 'Kennenlernen.', desc: 'Kein Formular-Ping-Pong, kein Skript. Ich höre zu, Sie erzählen von Ihrem Betrieb, Ihren Kunden und was Sie sich von einer neuen Website erhoffen.' },
              { n: '02', title: 'Ihre Wünsche.', desc: 'Was soll die Website leisten? Mehr Anfragen, mehr Vertrauen, mehr Sichtbarkeit? Wir schauen gemeinsam, was wirklich zu Ihnen und Ihrer Zielgruppe passt.' },
              { n: '03', title: 'Klarer Plan.', desc: 'Sie gehen mit einem konkreten Bild davon, wie Ihre Website aussehen und was sie kommunizieren soll. Ohne Verpflichtung – dafür mit echtem Mehrwert.' },
            ].map((step, idx) => (
              <div key={idx} className="grid lg:grid-cols-[1fr_2fr] gap-6 lg:gap-16 py-10 sm:py-12 lg:py-16 border-t border-gray-100">
                <div>
                  <span
                    className="ss-deco-number"
                    style={{
                      fontFamily: "'Outfit', sans-serif",
                      background: GRADIENT,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      opacity: 0.25,
                    }}
                  >
                    {step.n}
                  </span>
                </div>
                <div className="grid sm:grid-cols-2 gap-6 lg:gap-8 items-start">
                  <h3 className="text-2xl sm:text-3xl lg:text-4xl font-black text-gray-900 leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    {step.title}
                  </h3>
                  <p className="text-gray-500 text-sm sm:text-base lg:text-lg leading-relaxed sm:pt-2">{step.desc}</p>
                </div>
              </div>
            ))}
            <div className="border-t border-gray-100" />
          </div>
        </div>
      </section>

      {/* ── CALENDLY ── */}
      <section id="calendly-section" className="py-14 sm:py-24 lg:py-32 px-4 sm:px-6 bg-[#0e0e0e]">
        <div className="max-w-5xl mx-auto">
          <div className="mb-10 sm:mb-12 lg:mb-16">
            <p className="ss-label" style={{ color: '#14b8a6' }}>
              Terminbuchung
            </p>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-black text-white leading-[1.05]"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Wählen Sie Ihren{' '}
              <span style={{
                background: GRADIENT,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}>
                Wunschtermin.
              </span>
            </h2>
            <p className="text-gray-400 text-sm sm:text-base lg:text-lg mt-4 max-w-xl">
              Suchen Sie sich einfach einen passenden Slot aus – ich freue mich auf das Gespräch.
            </p>
          </div>

          <div
            className="overflow-hidden"
            style={{ border: '1px solid rgba(20,184,166,0.3)', borderRadius: '4px' }}
          >
            <div
              className="calendly-inline-widget"
              data-url="https://calendly.com/info-qwk/seitenschmied-kennenlernen"
              style={{ minWidth: '320px', height: '700px' }}
            ></div>
          </div>
        </div>
      </section>

      {/* ── KEINE VERPFLICHTUNGEN ── */}
      <section className="py-10 sm:py-16 lg:py-20 px-4 sm:px-6 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8 lg:gap-0 lg:flex lg:flex-wrap lg:items-center lg:justify-between">
            {[
              { icon: 'ri-shield-check-line', value: 'Keine Verpflichtung', desc: 'Vollständig unverbindlich' },
              { icon: 'ri-money-euro-circle-line', value: '100 % kostenlos', desc: 'Keinerlei versteckte Kosten' },
              { icon: 'ri-chat-smile-2-line', value: 'Ehrliche Beratung', desc: 'Ohne Verkaufsdruck' },
            ].map((item, idx) => (
              <>
                {idx > 0 && <div key={`div-${idx}`} className="w-px h-12 bg-gray-200 hidden lg:block"></div>}
                <div key={item.value} className="flex items-center gap-4 sm:gap-5">
                  <div
                    className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: GRADIENT }}
                  >
                    <i className={`${item.icon} text-lg sm:text-xl text-white`}></i>
                  </div>
                  <div>
                    <p className="font-black text-gray-900 text-base sm:text-lg" style={{ fontFamily: "'Outfit', sans-serif" }}>{item.value}</p>
                    <p className="text-xs sm:text-sm text-gray-400 font-medium">{item.desc}</p>
                  </div>
                </div>
              </>
            ))}
          </div>
        </div>
      </section>

      {/* ── KONTAKTFORMULAR ── */}
      <section id="lieber-schreiben" className="py-14 sm:py-24 lg:py-32 px-4 sm:px-6 bg-[#0e0e0e]">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-20 items-start">

            {/* Left: Copy */}
            <div>
              <p className="ss-label" style={{ color: '#14b8a6' }}>
                Alternative
              </p>
              <h2
                className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-black text-white leading-[1.05] mb-6 sm:mb-8"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                Lieber schreiben?<br />
                <span style={{
                  background: GRADIENT,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}>
                  Kein Problem.
                </span>
              </h2>
              <p className="text-gray-400 text-sm sm:text-base lg:text-lg leading-relaxed mb-8 sm:mb-10 max-w-md">
                Füllen Sie das Formular aus – ich melde mich innerhalb von 24 Stunden bei Ihnen.
              </p>

              <div className="space-y-6">
                {[
                  { icon: 'ri-phone-line', label: 'Rufen Sie uns an', value: '+49 160 7676763', href: 'tel:+491607676763' },
                  { icon: 'ri-mail-line', label: 'Schreiben Sie uns', value: 'info@seitenschmied.com', href: 'mailto:info@seitenschmied.com' },
                  { icon: 'ri-map-pin-line', label: 'Standort', value: 'Gräfelfing, Würmtal', href: undefined },
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-4">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ background: 'rgba(20,184,166,0.15)' }}
                    >
                      <i className={`${item.icon} text-base`} style={{ color: '#14b8a6' }}></i>
                    </div>
                    <div>
                      <p className="text-xs font-bold tracking-widest uppercase text-gray-500">{item.label}</p>
                      {item.href ? (
                        <a
                          href={item.href}
                          className="text-white font-semibold text-base hover:opacity-70 transition-opacity cursor-pointer"
                          onClick={item.href.startsWith('tel:') ? (e) => { e.preventDefault(); trackPhoneConversion(item.href!); } : undefined}
                        >
                          {item.value}
                        </a>
                      ) : (
                        <p className="text-white font-semibold text-base">{item.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Form */}
            <div className="bg-white p-6 sm:p-10" style={{ borderRadius: '2px' }}>
              <form onSubmit={handleSubmit} data-readdy-form className="space-y-5 sm:space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Ihr vollständiger Name"
                    className="w-full px-4 py-3 sm:py-3.5 text-sm border-2 border-gray-200 focus:outline-none transition-colors"
                    style={{ borderRadius: '2px' }}
                    onFocus={(e) => { e.target.style.borderColor = '#14b8a6'; }}
                    onBlur={(e) => { e.target.style.borderColor = '#e5e7eb'; }}
                  />
                </div>

                <div>
                  <p className="block text-sm font-semibold text-gray-700 mb-2">Wie darf ich Sie kontaktieren? *</p>
                  <div className="flex rounded overflow-hidden border-2 border-gray-200 mb-3" style={{ borderRadius: '2px' }}>
                    <button
                      type="button"
                      onClick={() => { setContactMethod('phone'); setFormData((p) => ({ ...p, email: '' })); }}
                      className="flex-1 flex items-center justify-center gap-2 py-3 text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap"
                      style={{ background: contactMethod === 'phone' ? GRADIENT : 'transparent', color: contactMethod === 'phone' ? '#fff' : '#6b7280' }}
                    >
                      <i className="ri-phone-line text-sm"></i>
                      Telefon
                    </button>
                    <button
                      type="button"
                      onClick={() => { setContactMethod('email'); setFormData((p) => ({ ...p, phone: '' })); }}
                      className="flex-1 flex items-center justify-center gap-2 py-3 text-sm font-semibold transition-all duration-200 cursor-pointer border-l-2 border-gray-200 whitespace-nowrap"
                      style={{ background: contactMethod === 'email' ? GRADIENT : 'transparent', color: contactMethod === 'email' ? '#fff' : '#6b7280' }}
                    >
                      <i className="ri-mail-line text-sm"></i>
                      E-Mail
                    </button>
                  </div>

                  {contactMethod === 'phone' ? (
                    <input
                      type="tel" id="phone" name="phone" required value={formData.phone} onChange={handleInputChange}
                      placeholder="+49 123 456789"
                      className="w-full px-4 py-3 sm:py-3.5 text-sm border-2 border-gray-200 focus:outline-none transition-colors"
                      style={{ borderRadius: '2px' }}
                      onFocus={(e) => { e.target.style.borderColor = '#14b8a6'; }}
                      onBlur={(e) => { e.target.style.borderColor = '#e5e7eb'; }}
                    />
                  ) : (
                    <input
                      type="email" id="email" name="email" required value={formData.email} onChange={handleInputChange}
                      placeholder="ihre@email.de"
                      className="w-full px-4 py-3 sm:py-3.5 text-sm border-2 border-gray-200 focus:outline-none transition-colors"
                      style={{ borderRadius: '2px' }}
                      onFocus={(e) => { e.target.style.borderColor = '#14b8a6'; }}
                      onBlur={(e) => { e.target.style.borderColor = '#e5e7eb'; }}
                    />
                  )}
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-semibold text-gray-700 mb-2">Betreff *</label>
                  <select
                    id="subject" name="subject" required value={formData.subject} onChange={handleInputChange}
                    className="w-full px-4 py-3 sm:py-3.5 text-sm border-2 border-gray-200 focus:outline-none transition-colors cursor-pointer appearance-none bg-white"
                    style={{ borderRadius: '2px' }}
                    onFocus={(e) => { e.target.style.borderColor = '#14b8a6'; }}
                    onBlur={(e) => { e.target.style.borderColor = '#e5e7eb'; }}
                  >
                    <option value="">Bitte wählen ...</option>
                    <option value="Ich möchte eine neue Website">Ich möchte eine neue Website</option>
                    <option value="Ich möchte meine Website relaunchen">Ich möchte meine Website relaunchen</option>
                    <option value="Ich möchte meine Website optimieren">Ich möchte meine Website optimieren</option>
                    <option value="Ich möchte besser gefunden werden (SEO)">Ich möchte besser gefunden werden (SEO)</option>
                    <option value="Ich möchte mehr Anfragen über meine Website">Ich möchte mehr Anfragen über meine Website</option>
                    <option value="Allgemeine Frage">Allgemeine Frage</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">Nachricht *</label>
                  <textarea
                    id="message" name="message" required value={formData.message} onChange={handleInputChange}
                    rows={5} maxLength={500}
                    placeholder="Erzählen Sie mir kurz von Ihrem Projekt ..."
                    className="w-full px-4 py-3 sm:py-3.5 text-sm border-2 border-gray-200 focus:outline-none transition-colors resize-none"
                    style={{ borderRadius: '2px' }}
                    onFocus={(e) => { e.target.style.borderColor = '#14b8a6'; }}
                    onBlur={(e) => { e.target.style.borderColor = '#e5e7eb'; }}
                  />
                  <div className="text-xs text-gray-400 mt-1 text-right">{formData.message.length}/500</div>
                </div>

                {formError && <p className="text-sm text-red-600 font-medium">{formError}</p>}
                <p className="text-xs text-gray-400">* Pflichtfelder</p>

                <button
                  type="submit"
                  disabled={formSubmitting}
                  className="w-full py-4 font-bold text-base text-white transition-all duration-300 hover:shadow-xl hover:scale-[1.02] whitespace-nowrap cursor-pointer disabled:opacity-60"
                  style={{ background: GRADIENT, borderRadius: '2px' }}
                >
                  {formSubmitting ? 'Wird gesendet ...' : 'Nachricht senden'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
